import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../../utils/firebase";
import moment from "moment"

/**
 * UI components
 */
import Table from "../../../components/structure/table/table";
import Log from "../../../components/booking/logs/log";

/**
 * Functional component to return the booking hotel panel
 */
function Logs(props) {
    const [logs, setLogs] = useState([]);
    const [timeline, setTimeline] = useState([])
    const [usersById, setUsersById] = useState({})

    /**
     * Deconstruct the client ID from the props
     */
    const { clientID } = props;

    const enrichUserNames = async (userIds) => {
        const unique = [...new Set((userIds || []).filter(Boolean))]
        if (unique.length === 0) return

        const missing = unique.filter((id) => !usersById[id])
        if (missing.length === 0) return

        const fetched = await Promise.all(
            missing.map(async (id) => {
                const snap = await db.doc(`users/${id}`).get()
                const data = snap.exists ? snap.data() : null
                const name = data?.first_name ? `${data.first_name} ${data.last_name}` : id
                return [id, name]
            })
        )

        setUsersById((prev) => {
            const next = { ...prev }
            fetched.forEach(([id, name]) => {
                next[id] = name
            })
            return next
        })
    }

    const formattedTimeline = useMemo(() => {
        return (timeline || []).map((t) => ({
            ...t,
            agentName: t.agent ? (usersById[t.agent] || t.agent) : "",
        }))
    }, [timeline, usersById])

    /**
     * On component load
     */
    useEffect(() => {
        /**
         * Setup a listener on the database to stream the logs down for this booking
         */
        const unsubscribe = db.collection(`clients/${clientID}/logs`)
            .orderBy("created", "desc")
            .onSnapshot((logDocs) => {
                /**
                 * Loop over the logs
                 */
                logDocs.docChanges().forEach((change) => {
                    /**
                     * Log added
                     */
                    if (change.type === "added") {
                        setLogs((logs) => [...logs, {
                            id: change.doc.id,
                            ...change.doc.data(),
                        }]);
                    }
                    /**
                     * Log updated
                     */
                    if (change.type === "modified") {
                        setLogs((logs) => {
                            let updatedLogs = [...logs];
                            for (let i in logs) {
                                if (logs[i].id === change.doc.id) {
                                    updatedLogs[i] = {
                                        id: change.doc.id,
                                        ...change.doc.data(),
                                    };
                                    break;
                                }
                            }
                            return updatedLogs;
                        });
                    }
                    /**
                     * Log removed
                     */
                    if (change.type === "removed") {
                        setLogs((logs) =>
                            logs.filter((log) => log.id !== change.doc.id));
                    }
                });
            });
        /**
         * Remove the listener when the component unloads
         */
        return () => unsubscribe();
    }, [clientID]);

    // Fetch enquiry timeline (open + removed) on mount
    useEffect(() => {
        const fetchTimeline = async () => {
            try {
                const [openSnap, removedSnap, bookingsSnap] = await Promise.all([
                    // Avoid orderBy to prevent composite-index requirements; sort client-side instead.
                    db.collection("enquiries").where("client", "==", clientID).get(),
                    db.collection("enquiries_removed").where("enquiry.client", "==", clientID).get(),
                    db.collection("bookings").where("client", "==", clientID).get(),
                ])

                const getSeconds = (ts) => (ts && typeof ts.seconds === "number" ? ts.seconds : null)

                // Build a lookup of enquiry.created.seconds -> booking
                const bookingsByEnquiryCreatedSeconds = new Map()
                bookingsSnap.docs.forEach((doc) => {
                    const data = doc.data()
                    const enquiryCreatedSeconds = getSeconds(data?.enquiry?.created)
                    if (!enquiryCreatedSeconds) return
                    bookingsByEnquiryCreatedSeconds.set(enquiryCreatedSeconds, {
                        id: doc.id,
                        reference: data?.reference || null,
                        created: data?.created || null,
                    })
                })

                const open = openSnap.docs.map((doc) => {
                    const d = doc.data()
                    return {
                        id: doc.id,
                        type: "OPEN",
                        opened: d.created,
                        removed: null,
                        agent: d.agent || null,
                        status: d.status || "",
                        location: d.location || "",
                        site: d.site || "",
                    }
                })

                const removed = removedSnap.docs.map((doc) => {
                    const d = doc.data()
                    const enquiry = d.enquiry || {}
                    const openedTs = enquiry.created || d.enquired || null
                    const openedSeconds = getSeconds(openedTs)
                    const bookingMatch = openedSeconds ? bookingsByEnquiryCreatedSeconds.get(openedSeconds) : null

                    return {
                        id: doc.id,
                        type: bookingMatch ? "BOOKED" : "REMOVED",
                        opened: openedTs,
                        removed: d.created || null,
                        agent: enquiry.agent || d.agent || null,
                        status: enquiry.status || d.status || "",
                        location: enquiry.location || "",
                        site: enquiry.site || d.site || "",
                        booking: bookingMatch || null,
                    }
                })

                const combined = [...open, ...removed].sort((a, b) => {
                    const aOpenedMs = a.opened?.seconds ? a.opened.seconds * 1000 : 0
                    const bOpenedMs = b.opened?.seconds ? b.opened.seconds * 1000 : 0
                    if (bOpenedMs !== aOpenedMs) return bOpenedMs - aOpenedMs

                    const aRemovedMs = a.removed?.seconds ? a.removed.seconds * 1000 : 0
                    const bRemovedMs = b.removed?.seconds ? b.removed.seconds * 1000 : 0
                    return bRemovedMs - aRemovedMs
                })

                setTimeline(combined)

                const agentIds = combined.map((x) => x.agent).filter(Boolean)
                enrichUserNames(agentIds)
            } catch (err) {
                console.error("Error fetching enquiry timeline:", err)
            }
        }

        if (clientID) fetchTimeline()
    }, [clientID])

    return (
        <>
            <Table
                className="logs-table"
                headings={[
                    "Event",
                    "When",
                    "User",
                    "Message",
                ]}>

                {logs.map((log) => (
                    <Log
                        key={log.id}
                        details={log} />
                ))}
            </Table>

            <div style={{ marginTop: "28px" }}>
                <div style={{ marginBottom: "16px" }}>
                    <small style={{ fontWeight: 600, color: "inherit" }}>Enquiry logs</small>
                </div>
                <Table
                    className="enquiries-table"
                    headings={[
                        "Type",
                        "Opened",
                        "Removed",
                        "Agent",
                        "Status",
                        "Location/Event",
                        "Site",
                    ]}
                    noResults={formattedTimeline.length === 0}
                    noResultsMessage="No enquiry history found for this client">
                    {formattedTimeline.map((t) => (
                        <tr key={`${t.type}-${t.id}`}>
                            <td>{t.type}</td>
                            <td>{t.opened?.seconds ? moment(t.opened.seconds * 1000).format("DD/MM/YYYY HH:mm") : <small>N/A</small>}</td>
                            <td>{t.removed?.seconds ? moment(t.removed.seconds * 1000).format("DD/MM/YYYY HH:mm") : <small>N/A</small>}</td>
                            <td>{t.agentName ? t.agentName : <small>Not yet assigned</small>}</td>
                            <td>{t.status ? t.status : <small>N/A</small>}</td>
                            <td>{t.location ? t.location : <small>N/A</small>}</td>
                            <td>{t.site ? t.site : <small>N/A</small>}</td>
                        </tr>
                    ))}
                </Table>
            </div>
        </>
    );
}

export default Logs;